import { MinzeElement, EventListeners, Reactive } from 'minze'
import MainModule from '../../modules/main'
import { Geo } from '../../modules/geo'
import { Locale } from '../../modules/locale'
import { Font } from '../../constants'
import { ConsoleInfo, ConsoleError } from '../../modules/logging'

Locale.getInstance().registerKeySet('fr', 'geoDrawer', {
  title: 'Sélectionner une ville',
  search: 'Rechercher une ville ou région',
  automatic: 'Localisation automatique',
  confirm: 'Confirmer',
})

Locale.getInstance().registerKeySet('en', 'geoDrawer', {
  title: 'Select the city',
  search: 'Type a city name or area',
  automatic: 'My location',
  confirm: 'Confirm',
})

export interface LvcGeoDrawer {
  locationList: any[]
  selectedIndex: number
}

export class LvcGeoDrawer extends MinzeElement {
  reactive: Reactive = [
    ['locationList', []],
    ['selectedIndex', -1],
    ['locationString', ''],
  ]

  loading: boolean = false
  hasValue: boolean = false
  automaticLocation: any = null
  initialSelectedId: any = null
  selectedId: any = null

  constructor() {
    super()
    this.locationList = []
    this.selectedIndex = -1
  }

  onReady() {
    this.initialize()
  }

  async initialize(): Promise<void> {
    this.loading = true
    this.rerender()
    this.locationList = []
    let geo = MainModule.getInstance().getGeo()

    if (!geo) {
      this.loading = false
      this.rerender()
      return
    }

    let currentGPS = await Geo.getGeo()
    let currentGPSLocation = null
    const currentLocation: any = geo.currentLocation

    if (currentGPS) {
      currentGPSLocation = await geo.getNearestLocation(
        currentGPS.latitude,
        currentGPS.longitude,
        50000
      )
    }

    if (currentGPSLocation && currentGPSLocation.id === currentLocation.id) {
      this.selectedIndex = -1
      this.initialSelectedId = currentLocation.id
      this.selectedId = currentLocation.id
    } else if (currentLocation) {
      const locationList = [await geo.getById(currentLocation.id)]
      this.locationList = locationList

      for (let i = 0; i < locationList.length; i++) {
        const location = locationList[i]

        if (location.id === currentLocation.id) {
          this.selectedIndex = i
          this.initialSelectedId = location.id
          this.selectedId = location.id
          break
        }
      }
    }

    // get automatic location of the user
    this.automaticLocation = await geo.getAutomaticLocation()

    // get locations near user
    this.locationList = await geo.listNearMe(30000)

    // remove location from the locationlist that has the same id as the automaticLocation
    if (this.automaticLocation) {
      this.locationList = this.locationList.filter(
        location => location.id !== this.automaticLocation.id
      )
    }

    this.loading = false
    this.rerender()
  }

  onClose() {
    this.remove()
  }

  close() {
    let drawer = this.select('lvc-drawer-modal')
    drawer?.setAttribute('close', 'true')
  }

  async onSearchInput(e: Event): Promise<void> {
    this.loading = true
    this.rerender()
    const input = e.target as HTMLInputElement
    const value = input.value
    this.hasValue = value.length > 0
    this.locationString = value
    const geo = MainModule.getInstance().getGeo()
    const locationList = geo ? await geo.list(value) : []
    this.selectedIndex = -1
    this.locationList = locationList
    this.loading = false

    this.rerender()
  }

  async save(): Promise<void> {
    this.loading = true
    this.rerender()

    let geo = MainModule.getInstance().getGeo()

    if (!geo) {
      this.close()
      return
    }

    if (this.selectedIndex === -1 && this.automaticLocation) {
      geo.currentLocation = this.automaticLocation
      this.close()
      return
    }

    if (this.selectedIndex === -1) {
      geo.currentLocation = null
      this.close()
      return
    }

    const location = this.locationList[this.selectedIndex]
    if (!location) {
      this.close()
      return
    }

    geo.currentLocation = location
    this.close()
  }

  async onSelectItem(e: Event) {
    const item = e.currentTarget as HTMLElement
    const index = item.getAttribute('data-index')
    let i = -1
    try {
      i = parseInt(index as string)
    } catch (e) {
      i = -1
    }
    if (i === this.selectedIndex) {
      this.selectedIndex = -1
    } else {
      this.selectedIndex = i
    }

    await this.updateSelectedId(this.selectedIndex === -1)

    this.rerender()
  }

  async updateSelectedId(forceAutomatic: boolean = false) {
    if (forceAutomatic) {
      if (this.automaticLocation) this.selectedId = this.automaticLocation.id
      else this.selectedId = -1
      await this.save()
      return
    }
    try {
      const location = this.locationList[this.selectedIndex]
      if (location) {
        this.selectedId = location.id
        await this.save()
        try {
          if (location.name) {
            await MainModule.getInstance().analyticsTrack('accueil_selecteur_lieu', {
              link_text: location.name,
            })
          }
        } catch (error) {
          console.error('Error tracking analytics:', error)
        }
      } else {
        ConsoleInfo(true, 'no location')
        this.selectedId = null
      }
    } catch (e) {
      ConsoleError(true, e)
    }
  }

  html() {
    let items: string[] = []
    if (this.locationList && this.locationList.length > 0) {
      this.locationList?.map((location, index) => {
        items.push(`
          <button data-index="${index}" class="lvc-drawer-item-button">
            <span class="location-name">
              ${location.name}
            </span>
              ${this.selectedIndex === index ? `<lvc-check-orange-icon></lvc-check-orange-icon>` : ''}
          </button>
          `)
      })
    }

    let automaticLocation = ''

    if (this.automaticLocation) {
      automaticLocation = `
          <button class="auto-location" data-index="-1">
            <div class="location-name">
              <span class="auto-location-title"> 
              ${Locale.getKey('geoDrawer.automatic')}
              </span>
              <span class="auto-location-name"> 
              ${this.automaticLocation.name} (${this.automaticLocation.county})
              </span>
            </div>
            ${this.selectedIndex === -1 && this.selectedId === this.automaticLocation.id ? `<lvc-check-orange-icon></lvc-check-orange-icon>` : ''}
          </button>
          <div class="or-divider">
            <div class="line"></div>
            <span>${Locale.getKey('general.or')}</span>
            <div class="line"></div>
          </div>
      `
    }

    return `
      <lvc-drawer-modal modal-title="${Locale.getKey('geoDrawer.title')}" class="lvc-geo-drawer">
        <div class="content">
          ${automaticLocation}
          <div class="search">
            <lvc-search-two-icon></lvc-search-two-icon>
            <input type="text" class="lvc-input" placeholder="${Locale.getKey('geoDrawer.search')}">
          </div>
          <div class="lvc-geo-drawer-list-container">
            <div class="lvc-geo-drawer-list">
              ${items.join('')}
            </div>
          </div>
        </div>
      </lvc-drawer-modal>
    `
  }

  css(): string {
    return `
      :host {
        font-family: ${Font.family};
        font-size: 14px;
        color: #000;
        position: relative;
      }

      .or-divider {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 10px 0;
      }

      .or-divider .line {
        width: 100%;
        height: 1px;
        background: rgba(30, 0, 125, 0.30);
      }

      .or-divider span {
        margin: 0 10px;
        color: #000;
        font-variant-numeric: lining-nums tabular-nums;
        font-feature-settings: 'clig' off, 'liga' off;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium}; 
        line-height: 120%; /* 16.8px */
      }

      .content {
        display: flex;
        flex-direction: column;
        height: 100%;
        padding: 10px 0;
        box-sizing: border-box;

      }

      .lvc-geo-drawer-list-container {
        flex: 1;
        overflow-y: hidden;
        padding: 10px;
        position: relative;
      }
      .lvc-geo-drawer-list {
        overflow-y: auto;
        height: 100%;
        display: flex;
        flex-direction: column;
        overflow-x: hidden;
      }

      .lvc-drawer-item-button, .auto-location {
        background: inherit;
        border: none;
        text-align: left;
        font-family: ${Font.family};
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px;
        cursor: pointer;
        line-height: 1.5;
        border-bottom: 1px solid #ccc;
        font-size: 14px;
        color: var(--brand-minuit, #1E007D);
        outline: none;
        transition: transform 0.3s ease-in-out, background 0.3s ease-in-out;

      }

      .lvc-drawer-item-button:hover {
        transform: translateX(5px);
        background: var(--brand-menthe, #DCFFD7);
      }

      div.location-name:hover {
        transform: translateX(5px);
      }

      .auto-location {
        border: none;
      }

      .search {
        font-family: ${Font.family};
        margin-top: 10px;
        display: flex;
        height: 40px;
        align-items: center;
        padding: 10px;
        border: 1px solid #1E007D;
        border-radius: 50px;
        background-color: ${this.hasValue ? 'var(--brand-ciel, #BFF0FF)' : 'transparent'};
      }

      .search:focus-within {
        background: var(--brand-menthe, #DCFFD7);
      }

      .search input {
        background-color: transparent;
        font-family: ${Font.family};
        border: none;
        outline: none;
        padding: 10px;
        width: 100%;
        font-size: 16px !important;
      }

      .auto-location {
        padding: 0;
      }

      .auto-location .location-name {
        display: flex;
        flex-direction: column;
        transition: transform 0.3s ease;
      } 

      .auto-location-title {
        color: var(--brand-minuit, #1E007D);
        font-variant-numeric: lining-nums tabular-nums;
        font-feature-settings: 'clig' off, 'liga' off;

        /* D/textes/grands textes */
        font-family: ${Font.family};
        font-size: 20px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 30px */
      }

      .auto-location-name {
        color: var(--brand-minuit, #1E007D);
        font-variant-numeric: lining-nums tabular-nums;
        font-feature-settings: 'clig' off, 'liga' off;

        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 120%; /* 16.8px */
    }


    `
  }

  eventListeners: EventListeners = [
    ['lvc-drawer-modal', 'close', this.onClose.bind(this)],
    ['.lvc-input', 'keyup', this.onSearchInput.bind(this)],
    ['.lvc-drawer-item-button', 'click', this.onSelectItem.bind(this)],
    ['.auto-location', 'click', this.onSelectItem.bind(this)],
  ]
}
